import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import { track, ANALYTICS_EVENTS } from '../lib/analytics';
import { Helmet } from 'react-helmet-async';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-white/8 last:border-0">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-5 md:py-6 text-left group"
      aria-expanded={isOpen}
    >
      <span className="text-base md:text-lg font-medium text-white pr-8 group-hover:text-accent-300 transition-colors">
        {question}
      </span>
      <ChevronDownIcon
        className={`w-5 h-5 text-gray-300 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-accent-400' : ''}`}
      />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <p className="text-gray-300 text-sm md:text-base leading-relaxed pb-5 md:pb-6 pr-12">
            {answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
    { q: t('faq.q6'), a: t('faq.a6') },
  ];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };

  return (
    <section className="py-20 md:py-28 px-4 relative overflow-hidden">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>

      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 grid-pattern opacity-10" />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="eyebrow text-accent-400 text-xs mb-3">
            {t('faq.label')}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold mb-5">
            {t('faq.title')}{' '}
            <span className="text-accent-400">
              {t('faq.titleHighlight')}
            </span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="surface rounded-2xl px-6 md:px-8"
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === index}
              onClick={() => {
                // Which questions get opened is a map of what the page failed
                // to answer above the fold.
                if (openIndex !== index) track(ANALYTICS_EVENTS.FAQ_OPENED, { question: faq.q });
                setOpenIndex(openIndex === index ? -1 : index);
              }}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
